/**
 * Kotlin protocol generator - Functional implementation with dependency injection
 * Generates Kotlin interfaces from parsed module definitions
 */

import type { ParsedModule } from "../../parser";
import { isAnyObjectType } from "../../utils/type-helpers";
import type { KotlinCodeEmitter } from "./kotlin-code-emitter";
import type { KotlinTypeMapper } from "./kotlin-type-mapper";

export interface KotlinProtocolGeneratorDeps {
  typeMapper: KotlinTypeMapper;
  codeEmitter: KotlinCodeEmitter;
}

export interface KotlinProtocolGenerator {
  /**
   * Generates Kotlin interface for library mode (public API)
   */
  generateLibraryModuleInterface(module: ParsedModule): string;

  /**
   * Generates event helper extension functions for Kotlin
   */
  generateKotlinEventHelpers(module: ParsedModule): string;
}

/**
 * Creates a Kotlin protocol generator with injected dependencies
 */
export function createKotlinProtocolGenerator(
  deps: KotlinProtocolGeneratorDeps
): KotlinProtocolGenerator {
  const { typeMapper, codeEmitter } = deps;

  /**
   * Generates Kotlin interface for library mode (public API)
   */
  function generateLibraryModuleInterface(module: ParsedModule): string {
    const constants = module.constants
      .map((constant) => {
        const kotlinType = typeMapper.mapTypeScriptToKotlinWithInterfaces(
          constant.type
        );
        const comment = constant.jsDocComment
          ? `${codeEmitter.generateKotlinMultilineDocComment(constant.jsDocComment.split('\n'))}\n`
          : "";
        return `${comment}val ${constant.name}: ${kotlinType}`;
      })
      .join("\n");

    let needsReactBridgeMapImport = false;
    let needsReactBridgeArrayImport = false;

    const methods = module.methods
      .map((method) => {
        const params = (method.params || [])
          .map((param) => {
            // If parser marked this as a function/callback, emit function type using parameter types
            if (param.functionType) {
              const argTypes = (param.functionType.paramTypes || []).map(
                (t: string, idx: number) => {
                  const tt = t.trim();
                  // Arrays in callback args
                  if (tt.endsWith("[]")) {
                    const elem = tt.slice(0, -2).trim();
                    // Special-case: JS callback where a single parameter is a rest any[] (e.g., (...args: any[]))
                    if (
                      param.functionType?.paramRestFlags?.[idx] &&
                      elem === "any"
                    ) {
                      return "Array<Any?>";
                    }
                    // If this param is a rest parameter and AnyObject[] -> List<WritableMap>
                    if (
                      param.functionType?.paramRestFlags?.[idx] &&
                      isAnyObjectType(elem)
                    ) {
                      needsReactBridgeMapImport = true;
                      return "List<WritableMap>";
                    }
                    // Otherwise use WritableArray (bridge type)
                    needsReactBridgeArrayImport = true;
                    return "WritableArray";
                  }
                  // Single AnyObject -> WritableMap (bridge type)
                  if (isAnyObjectType(tt)) {
                    needsReactBridgeMapImport = true;
                    return "WritableMap";
                  }
                  // Inline object -> WritableMap
                  if (tt.startsWith("{")) {
                    needsReactBridgeMapImport = true;
                    return "WritableMap";
                  }
                  // PascalCase interface or primitives -> keep mapped type (typed interface allowed)
                  return typeMapper.mapTypeScriptToKotlinWithInterfaces(tt);
                }
              );
              const argJoined = argTypes.join(", ");
              return `${param.name}: (${argJoined}) -> Unit`;
            }

            const t = (param.type || "").trim();
            // Map '{}' or 'AnyObject' (and 'any') explicitly to ReadableMap for Spec interface
            if (isAnyObjectType(t)) {
              needsReactBridgeMapImport = true;
              const optional = param.optional ? "?" : "";
              return `${param.name}: ReadableMap${optional}`;
            }

            const kotlinType =
              typeMapper.mapTypeScriptToKotlinWithInterfaces(t);
            const optional = param.optional ? "?" : "";
            return `${param.name}: ${kotlinType}${optional}`;
          })
          .join(", ");

        const returnType = typeMapper.mapTypeScriptToKotlinWithInterfaces(
          method.returnType
        );

        // Check if return type needs WritableMap/ReadableMap import
        if (returnType.includes("WritableMap") || returnType.includes("ReadableMap")) {
          needsReactBridgeMapImport = true;
        }
        if (returnType.includes("WritableArray") || returnType.includes("ReadableArray")) {
          needsReactBridgeArrayImport = true;
        }

        const suspendKeyword = method.isAsync ? "suspend " : "";
        const comment = method.jsDocComment
          ? `${codeEmitter.generateKotlinMultilineDocComment(method.jsDocComment.split('\n'))}\n`
          : "";

        return `${comment}${suspendKeyword}fun ${method.name}(${params}): ${returnType}`;
      })
      .join("\n");

    const constantsSection =
      constants.length > 0
        ? `
    ${codeEmitter.generateMarkComment("Module Constants")}

${codeEmitter.indent(constants)}

`
        : "";

    const methodsSection =
      methods.length > 0
        ? `
    ${codeEmitter.generateMarkComment("Module Methods")}

${codeEmitter.indent(methods)}
`
        : "";

    // Generate getConstants() override if module has constants
    const getConstantsOverride =
      constants.length > 0
        ? `

    // Override getConstants to provide module constants
    override fun getConstants(): Map<String, Any> = mapOf(
${module.constants.map((c) => `        "${c.name}" to ${c.name}`).join(",\n")}
    )`
        : "";

    // Get Android package info from scanned module
    const androidPackage =
      module.sourceModule.brickConfig?.android?.package ||
      "com.brickmodule.codegen";

    const extraImports = [
      needsReactBridgeMapImport
        ? "import com.facebook.react.bridge.ReadableMap\nimport com.facebook.react.bridge.WritableMap"
        : "",
      needsReactBridgeArrayImport
        ? "import com.facebook.react.bridge.ReadableArray\nimport com.facebook.react.bridge.WritableArray"
        : "",
    ]
      .filter(Boolean)
      .join("\n");

    const imports = ["import com.brickmodule.BrickModuleBase"];
    if (extraImports) {
      imports.push(extraImports);
    }

    return `${codeEmitter.generateFileHeader()}

package ${androidPackage}

${imports.join("\n")}

/**
 * Generated interface for ${module.moduleName} module
 * Implement this interface to create a type-safe ${module.moduleName} module
 */
interface ${module.moduleName}Spec : BrickModuleBase {
    companion object {
        const val MODULE_NAME = "${module.moduleName}"
    }

${constantsSection}${methodsSection}${getConstantsOverride}
}
`;
  }

  /**
   * Generates event helper extension functions for Kotlin (similar to Swift protocol extensions)
   */
  function generateKotlinEventHelpers(module: ParsedModule): string {
    if (!module.events || module.events.length === 0) {
      return "";
    }

    const helpers = module.events
      .map((e) => {
        const cap = e.name.charAt(0).toUpperCase() + e.name.slice(1);
        const eventKey = `on${cap}`;
        return `/**
 * Emit ${e.name} event to JavaScript
 * @param payload Event payload data
 */
fun ${module.moduleName}Spec.emitOn${cap}(payload: Map<String, Any>) {
    if (this is com.brickmodule.BrickModuleSpec) {
        this.emitEvent("${eventKey}", payload)
    }
}`;
      })
      .join("\n\n");

    return `

// Typed Event Emit Helpers for ${module.moduleName}
${helpers}
`;
  }

  return {
    generateLibraryModuleInterface,
    generateKotlinEventHelpers,
  };
}
